export interface ISsrDebugClientConfig {
    localStorageKey: string;
    cookieName: string;
    consolePrefix: string;
    isEnvironmentAllowed: () => boolean;
}

export interface ISsrDebugClient {
    isEnabled: () => boolean;
    syncCookie: () => void;
    enable: () => void;
    disable: () => void;
}

/**
 * Builds the browser-side half of an SSR debug logger: a localStorage flag
 * mirrored into a cookie (so the server-side collector can read debug
 * intent), plus enable/disable helpers that reload the page.
 *
 * @example
 * export const { isEnabled, syncCookie, enable, disable } = createSsrDebugClient({
 *   localStorageKey: 'MY_APP_SSR_DEBUG_MODE',
 *   cookieName: 'my-app-ssr-debug',
 *   consolePrefix: '[MyAppSSR]',
 *   isEnvironmentAllowed,
 * });
 */
export const createSsrDebugClient = (config: ISsrDebugClientConfig): ISsrDebugClient => {
    const isEnabled = (): boolean => {
        if (!config.isEnvironmentAllowed()) return false;
        try {
            return localStorage.getItem(config.localStorageKey) === 'true';
        } catch {
            return false;
        }
    };

    const syncCookie = (): void => {
        if (typeof document === 'undefined') return;
        if (isEnabled()) {
            document.cookie = `${config.cookieName}=true; path=/`;
        } else {
            document.cookie = `${config.cookieName}=; path=/; max-age=0`;
        }
    };

    const enable = (): void => {
        if (!config.isEnvironmentAllowed()) {
            console.warn(`${config.consolePrefix} disabled outside allowed environments`);
            return;
        }
        localStorage.setItem(config.localStorageKey, 'true');
        syncCookie();
        window.location.reload();
    };

    const disable = (): void => {
        localStorage.removeItem(config.localStorageKey);
        syncCookie();
        window.location.reload();
    };

    return { isEnabled, syncCookie, enable, disable };
};
